import React, { useState } from "react";
import useCedentes from "../../hooks/useCedentes";
import { userService } from "../../services/user.service";
import { toast } from "sonner";
import useAdmin from "../../hooks/useAdmin";
import useDebounce from "../../hooks/useDebounce";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Search, Loader2 } from "lucide-react";
import { Card } from "../ui/card";

export function AdminView({ onUserSelect }) {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);
  const { cedentes, setCedentes, isLoading, error } = useCedentes();
  const { handleUserSelect } = useAdmin();
  const [loadingId, setLoadingId] = useState(null);
  const [togglingCpf, setTogglingCpf] = useState(null);
  const { toggleCedenteActive } = userService;

  const filteredCedentes = (cedentes || []).filter(
    (cedente) =>
      cedente.nome.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      (cedente.cpf && cedente.cpf.includes(debouncedSearchTerm))
  );

  const handleSelect = async (cedente) => {
    try {
      setLoadingId(cedente.id);
      await handleUserSelect(cedente, onUserSelect);
    } finally {
      setLoadingId(null);
    }
  };

  const handleToggleActive = async (id) => {
    if (togglingCpf) return;
    try {
      setTogglingCpf(id);
      const res = await toggleCedenteActive(id);
      toast.success(res.ativo ? "Substituído ativado!" : "Substituído desativado!");
      
      // Update local state instead of full refresh
      setCedentes(prev => prev.map(c => 
        c.id === id ? { ...c, ativo: res.ativo } : c
      ));
    } catch (e) {
      toast.error("Erro ao alterar status: " + e.message);
    } finally {
      setTogglingCpf(null);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center py-12 text-muted-foreground">Carregando...</div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12 text-red-600">
          Erro ao carregar dados: {error}
        </div>
      );
    }

    if (filteredCedentes.length === 0) {
      return (
        <div className="text-center py-12 text-muted-foreground">
          <p>Nenhum substituído encontrado.</p>
        </div>
      );
    }

    return (
      <div className="rounded-xl overflow-hidden shadow-md border border-border">
        <Table className="text-base text-foreground">
          <TableHeader className="bg-muted">
            <TableRow>
              <TableHead className="text-muted-foreground font-bold text-sm py-4 uppercase tracking-wider">
                Nome do Substituído
              </TableHead>
              <TableHead className="text-muted-foreground font-bold text-sm py-4 uppercase tracking-wider">
                CPF
              </TableHead>
              <TableHead className="text-center text-muted-foreground font-bold text-sm py-4 uppercase tracking-wider">
                Ativo
              </TableHead>
              <TableHead className="text-right text-muted-foreground font-bold text-sm py-4 uppercase tracking-wider">
                Ação
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCedentes.map((cedente) => (
              <TableRow
                key={cedente.id}
                className="hover:bg-muted/80 transition-colors"
              >
                <TableCell className="font-semibold text-foreground py-4">
                  {cedente.nome}
                </TableCell>
                <TableCell className="text-muted-foreground py-4 tabular-nums">
                  {cedente.mask_cpf || cedente.cpf}
                </TableCell>
                <TableCell className="text-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleActive(cedente.id);
                    }}
                    disabled={togglingCpf === cedente.id}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cessaoYellow/20 focus:ring-offset-2 ${
                      cedente.ativo ? 'bg-green-600' : 'bg-gray-200'
                    } ${togglingCpf === cedente.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    aria-label={cedente.ativo ? "Desativar" : "Ativar"}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
                        cedente.ativo ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="outline"
                    size="default"
                    disabled={loadingId === cedente.id}
                    onClick={() => handleSelect(cedente)}
                    className="border-cessaoYellow text-cessaoYellow hover:bg-cessaoYellow/5 h-9 md:h-10 px-4 md:px-5 text-sm font-bold disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                  >
                    {loadingId === cedente.id ? (
                      <span className="inline-flex items-center gap-2">
                        <svg
                          className="animate-spin h-4 w-4 text-cessaoYellow"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          ></path>
                        </svg>
                        Carregando...
                      </span>
                    ) : (
                      "Ver Detalhes"
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="p-4 md:p-8 w-full">
      <div className="max-w-6xl mx-auto">
        <Card className="p-8 md:p-10 shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
          <div className="mb-10">
            <h2 className="text-xl font-bold text-foreground tracking-tight">
              Painel de Administrador
            </h2>
          </div>

          <div className="flex gap-3 mb-6">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome ou CPF..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-2 border-border focus:border-cessaoYellow transition-colors h-11 focus:ring-2 focus:ring-cessaoYellow/10"
              />
              {searchTerm !== debouncedSearchTerm && (
                <div className="absolute right-3 top-2.5 flex items-center text-xs text-muted-foreground gap-1 animate-pulse">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Buscando...
                </div>
              )}
            </div>
            <Button
              className="gap-2 bg-cessaoYellow hover:bg-cessaoYellow/90 text-white h-11 px-6 shadow-sm"
            >
              <Search className="w-4 h-4" />
              Buscar
            </Button>
          </div>

          {renderContent()}
        </Card>
      </div>
    </div>
  );
}
